/**
 * Deck-related runtime utilities, constants, and helper functions.
 *
 * This module contains runtime code (constants, helpers) extracted from types/deck.ts.
 * For type definitions, see types/deck.ts.
 */
import type { Rarity } from '@/types/card';
import type { DeckFormat, DeckStatus, DeckZone, FormatRules } from '@/types/deck';

// ============================================================================
// Format Rules
// ============================================================================

export const FORMAT_RULES: Record<DeckFormat, FormatRules> = {
  standard: {
    format: 'standard',
    min_deck_size: 60,
    max_deck_size: null,
    max_sideboard: 15,
    max_copies: 4,
    requires_commander: false,
    check_color_identity: false,
    rarity_restriction: null,
    has_sideboard: true,
    is_limited: false,
  },
  pioneer: {
    format: 'pioneer',
    min_deck_size: 60,
    max_deck_size: null,
    max_sideboard: 15,
    max_copies: 4,
    requires_commander: false,
    check_color_identity: false,
    rarity_restriction: null,
    has_sideboard: true,
    is_limited: false,
  },
  modern: {
    format: 'modern',
    min_deck_size: 60,
    max_deck_size: null,
    max_sideboard: 15,
    max_copies: 4,
    requires_commander: false,
    check_color_identity: false,
    rarity_restriction: null,
    has_sideboard: true,
    is_limited: false,
  },
  legacy: {
    format: 'legacy',
    min_deck_size: 60,
    max_deck_size: null,
    max_sideboard: 15,
    max_copies: 4,
    requires_commander: false,
    check_color_identity: false,
    rarity_restriction: null,
    has_sideboard: true,
    is_limited: false,
  },
  vintage: {
    format: 'vintage',
    min_deck_size: 60,
    max_deck_size: null,
    max_sideboard: 15,
    max_copies: 4, // Note: restricted cards handled separately
    requires_commander: false,
    check_color_identity: false,
    rarity_restriction: null,
    has_sideboard: true,
    is_limited: false,
  },
  pauper: {
    format: 'pauper',
    min_deck_size: 60,
    max_deck_size: null,
    max_sideboard: 15,
    max_copies: 4,
    requires_commander: false,
    check_color_identity: false,
    rarity_restriction: 'common',
    has_sideboard: true,
    is_limited: false,
  },
  commander: {
    format: 'commander',
    min_deck_size: 100,
    max_deck_size: 100,
    max_sideboard: 0,
    max_copies: 1,
    requires_commander: true,
    check_color_identity: true,
    rarity_restriction: null,
    has_sideboard: false,
    is_limited: false,
  },
  brawl: {
    format: 'brawl',
    min_deck_size: 60,
    max_deck_size: 60,
    max_sideboard: 0,
    max_copies: 1,
    requires_commander: true,
    check_color_identity: true,
    rarity_restriction: null,
    has_sideboard: false,
    is_limited: false,
  },
  oathbreaker: {
    format: 'oathbreaker',
    min_deck_size: 60,
    max_deck_size: 60,
    max_sideboard: 0,
    max_copies: 1,
    requires_commander: true, // Planeswalker + signature spell
    check_color_identity: true,
    rarity_restriction: null,
    has_sideboard: false,
    is_limited: false,
  },
  casual: {
    format: 'casual',
    min_deck_size: 0,
    max_deck_size: null,
    max_sideboard: 100,
    max_copies: 100,
    requires_commander: false,
    check_color_identity: false,
    rarity_restriction: null,
    has_sideboard: true,
    is_limited: false,
  },
  // Limited formats
  draft: {
    format: 'draft',
    min_deck_size: 40,
    max_deck_size: null, // No max, but we warn if over 41
    max_sideboard: 999, // Effectively unlimited (all undrafted cards)
    max_copies: 999, // No copy restriction in limited
    requires_commander: false,
    check_color_identity: false,
    rarity_restriction: null,
    has_sideboard: true,
    is_limited: true,
  },
  sealed: {
    format: 'sealed',
    min_deck_size: 40,
    max_deck_size: null,
    max_sideboard: 999, // Effectively unlimited
    max_copies: 999, // No copy restriction in limited
    requires_commander: false,
    check_color_identity: false,
    rarity_restriction: null,
    has_sideboard: true,
    is_limited: true,
  },
  jumpstart: {
    format: 'jumpstart',
    min_deck_size: 40,
    max_deck_size: null,
    max_sideboard: 999, // Effectively unlimited
    max_copies: 999, // No copy restriction in limited
    requires_commander: false,
    check_color_identity: false,
    rarity_restriction: null,
    has_sideboard: true,
    is_limited: true,
  },
};

// ============================================================================
// Display Labels
// ============================================================================

export const DECK_STATUS_LABELS: Record<DeckStatus, string> = {
  brewing: 'Brewing',
  active: 'Active',
  archived: 'Archived',
};

export const DECK_STATUS_COLORS: Record<DeckStatus, string> = {
  brewing: 'amber', // Yellow/orange for work-in-progress
  active: 'green', // Green for ready to play
  archived: 'slate', // Gray for inactive
};

export const DECK_FORMAT_LABELS: Record<DeckFormat, string> = {
  standard: 'Standard',
  pioneer: 'Pioneer',
  modern: 'Modern',
  legacy: 'Legacy',
  vintage: 'Vintage',
  pauper: 'Pauper',
  commander: 'Commander',
  brawl: 'Brawl',
  oathbreaker: 'Oathbreaker',
  casual: 'Casual',
  draft: 'Draft',
  sealed: 'Sealed',
  jumpstart: 'Jump Start',
};

export const DECK_ZONE_LABELS: Record<DeckZone, string> = {
  commander: 'Commander',
  companion: 'Companion',
  main: 'Main Deck',
  sideboard: 'Sideboard',
  maybeboard: 'Maybeboard',
  tokens: 'Tokens',
};

export const DECK_ZONE_ORDER: DeckZone[] = ['commander', 'companion', 'main', 'sideboard', 'maybeboard', 'tokens'];

// ============================================================================
// Basic Land Names
// ============================================================================

/** Basic lands that can have unlimited copies */
export const BASIC_LAND_NAMES = ['Plains', 'Island', 'Swamp', 'Mountain', 'Forest', 'Wastes'] as const;

/** Snow basics also unlimited */
export const SNOW_BASIC_LAND_NAMES = [
  'Snow-Covered Plains',
  'Snow-Covered Island',
  'Snow-Covered Swamp',
  'Snow-Covered Mountain',
  'Snow-Covered Forest',
] as const;

/** All basic lands (regular + snow) */
export const ALL_BASIC_LANDS = [...BASIC_LAND_NAMES, ...SNOW_BASIC_LAND_NAMES] as const;

// ============================================================================
// Derived Constants (computed from FORMAT_RULES)
// ============================================================================

/** Formats that use commander rules (requires commander, singleton, color identity) */
export const COMMANDER_FORMATS: DeckFormat[] = (Object.keys(FORMAT_RULES) as DeckFormat[]).filter(
  (format) => FORMAT_RULES[format].requires_commander,
);

/** Limited formats (draft, sealed, jumpstart) */
export const LIMITED_FORMATS: DeckFormat[] = (Object.keys(FORMAT_RULES) as DeckFormat[]).filter(
  (format) => FORMAT_RULES[format].is_limited,
);

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if a format uses commander rules (singleton, color identity checks)
 */
export function isCommanderFormat(format: DeckFormat | null): boolean {
  if (!format) return false;
  return FORMAT_RULES[format]?.requires_commander ?? false;
}

/**
 * Check if a format is a limited format (draft/sealed/jumpstart)
 */
export function isLimitedFormat(format: DeckFormat | null): boolean {
  if (!format) return false;
  return FORMAT_RULES[format]?.is_limited ?? false;
}

/**
 * Check if a format enforces color identity restrictions
 */
export function checksColorIdentity(format: DeckFormat | null): boolean {
  if (!format) return false;
  return FORMAT_RULES[format]?.check_color_identity ?? false;
}

/**
 * Get the format rules for a given format, or null if format is invalid/null
 */
export function getFormatRules(format: DeckFormat | null): FormatRules | null {
  if (!format) return null;
  return FORMAT_RULES[format] ?? null;
}

/**
 * Check if a card name is a basic land (can have unlimited copies)
 */
export function isBasicLand(cardName: string): boolean {
  return (ALL_BASIC_LANDS as readonly string[]).includes(cardName);
}

/**
 * Get rarity restriction for a format, or null if no restriction
 */
export function getFormatRarityRestriction(format: DeckFormat | null): Rarity | null {
  if (!format) return null;
  return FORMAT_RULES[format]?.rarity_restriction ?? null;
}
